/**
 * AIGenerateDialog Component
 * Dialog for generating action images using AI based on the favourite character.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { favouriteCharacterService } from '../services/favouriteCharacterService';

interface AIGenerateDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerated: (imageUrl: string, imageFile?: File) => void;
  deviceId: string;
  actionId?: string; // Required for existing actions, optional for new ones
  actionName: string;
  actionDescription: string;
  onCreateAction?: () => Promise<string>; // Callback to create action if it doesn't exist, returns new action ID
}

export const AIGenerateDialog: React.FC<AIGenerateDialogProps> = ({
  open,
  onClose,
  onGenerated,
  deviceId,
  actionId,
  actionName,
  actionDescription,
  onCreateAction,
}) => {
  const { t } = useTranslation();

  // State
  const [promptHint, setPromptHint] = useState('');
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [autoSavePhase, setAutoSavePhase] = useState<'creating' | null>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setPromptHint('');
      setGeneratedImageUrl(null);
      setError(null);
      setSuccessMessage(null);
    }
  }, [open]);

  // Check if action name is provided
  const hasActionName = actionName && actionName.trim().length > 0;

  // Handle image generation
  const handleGenerate = async () => {
    if (!hasActionName) {
      setError(t('actions.ai.nameRequired'));
      return;
    }

    // Get or create action ID
    let currentActionId = actionId;

    if (!currentActionId) {
      // No action ID - we need to auto-create
      if (!onCreateAction) {
        setError(t('actions.ai.saveFirst'));
        return;
      }

      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      setAutoSavePhase('creating');

      try {
        currentActionId = await onCreateAction();
        setAutoSavePhase(null);
      } catch (err) {
        console.error('Failed to create action:', err);
        setError(err instanceof Error ? err.message : t('actions.ai.createFailed'));
        setAutoSavePhase(null);
        setLoading(false);
        return;
      }
    } else {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
    }

    try {
      const response = await favouriteCharacterService.generateActionAIImage(
        deviceId,
        currentActionId,
        actionName,
        actionDescription || actionName, // Use name as description if not provided
        promptHint || undefined
      );

      // Add cache-busting parameter to prevent browser from showing cached image
      const cacheBuster = `?t=${Date.now()}`;
      setGeneratedImageUrl(response.picture_url + cacheBuster);
      setSuccessMessage(response.message);
    } catch (err) {
      console.error('Failed to generate AI image:', err);
      setError(err instanceof Error ? err.message : t('actions.ai.generationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle using the generated image
  const handleUseImage = () => {
    if (generatedImageUrl) {
      // The image is already uploaded to the action by the backend
      // Just close the dialog and notify parent
      onGenerated(generatedImageUrl);
    }
  };

  // Handle regeneration
  const handleRegenerate = () => {
    setGeneratedImageUrl(null);
    setSuccessMessage(null);
    handleGenerate();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('actions.pictureSource.aiTitle')}</DialogTitle>
      <DialogContent>
        {/* Warning if no action name */}
        {!hasActionName && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
            {t('actions.ai.nameRequired')}
          </Alert>
        )}

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success message */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {/* Main content - always show prompt field, image preview below */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Description */}
          <Typography variant="body2" color="text.secondary">
            {t('actions.ai.description')}
          </Typography>

          {/* Action info display */}
          <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('actions.ai.actionInfo')}
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {actionName || t('actions.ai.noName')}
            </Typography>
            {actionDescription && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {actionDescription}
              </Typography>
            )}
          </Box>

          {/* Prompt hint field - always visible for refinement */}
          <TextField
            label={t('actions.ai.promptHint')}
            placeholder={t('actions.ai.promptHintPlaceholder')}
            multiline
            rows={3}
            value={promptHint}
            onChange={(e) => setPromptHint(e.target.value)}
            disabled={loading || !hasActionName}
            fullWidth
            helperText={t('actions.ai.promptHintHelper')}
          />

          {/* Generate/Regenerate button or loading state */}
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 4,
              }}
            >
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography color="text.secondary">
                {autoSavePhase === 'creating'
                  ? t('actions.ai.autoSaving')
                  : t('actions.ai.generating')}
              </Typography>
            </Box>
          ) : (
            <Button
              variant="contained"
              startIcon={generatedImageUrl ? <RefreshIcon /> : <AIIcon />}
              onClick={generatedImageUrl ? handleRegenerate : handleGenerate}
              disabled={!hasActionName || (!actionId && !onCreateAction)}
              size="large"
              fullWidth
            >
              {generatedImageUrl ? t('actions.ai.regenerate') : t('actions.ai.generate')}
            </Button>
          )}

          {/* Generated image preview - shown below when available */}
          {generatedImageUrl && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 1 }}>
              <Box
                component="img"
                src={generatedImageUrl}
                alt={t('actions.ai.generatedImage')}
                sx={{
                  maxWidth: '100%',
                  maxHeight: 400,
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        {generatedImageUrl && (
          <Button
            variant="contained"
            onClick={handleUseImage}
            disabled={loading}
          >
            {t('actions.ai.useImage')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
